import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@material-ui/icons/Add';
import { Route } from 'react-router-dom';
import { getAllProperties } from '../PropertyModule/shared/services/data.service';
import { APP_ROUTES } from '../shared/config/routes-config';
import { getAllAreas, getAllCity } from '../shared/services/data/lookups.data';
import api from '../shared/services/utils/api/api.service';
import { AddPromoCodeModal } from './components/AddPromoCodeModal';
import { PromoCodesTable } from './components/PromoCodesTable';
import { emptyLookupOptions } from './constants';
import { isLookupList, mapLookupOptions, mapPromotionToRow } from './helpers';
import { ApplyToLookupOptions, PromoCodeRow } from './types';

export const PromocodeModule = () => (
  <Route exact path={APP_ROUTES.PROMOCODE_MODULE.PATH} component={PromocodeContainer} />
);

type PromoLookupResult = {
  options: ApplyToLookupOptions;
  hasError: boolean;
};

async function loadPromoLookupOptions(): Promise<PromoLookupResult> {
  const [areas, cities, properties] = await Promise.all([
    getAllAreas('PromocodeModule'),
    getAllCity('PromocodeModule'),
    getAllProperties()
  ]);

  return {
    options: {
      area: isLookupList(areas) ? mapLookupOptions(areas) : [],
      city: isLookupList(cities) ? mapLookupOptions(cities) : [],
      property: isLookupList(properties) ? mapLookupOptions(properties) : []
    },
    hasError: !isLookupList(areas) || !isLookupList(cities) || !isLookupList(properties)
  };
}

function PromocodeContainer() {
  const [isAddPromoModalOpen, setIsAddPromoModalOpen] = useState(false);
  const [lookupOptions, setLookupOptions] = useState<ApplyToLookupOptions>(emptyLookupOptions);
  const [isLookupsLoading, setIsLookupsLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [promoCodeRows, setPromoCodeRows] = useState<PromoCodeRow[]>([]);
  const [isPromotionsLoading, setIsPromotionsLoading] = useState(false);

  const loadPromotions = useCallback(async () => {
    setIsPromotionsLoading(true);

    try {
      const [response, lookupResult] = await Promise.all([
        api.getPromotions(),
        loadPromoLookupOptions()
      ]);
      const promotions = (response.data as any)?.data;

      setLookupOptions(lookupResult.options);

      if (lookupResult.hasError) {
        setLookupError('Some lookup options could not be loaded.');
      }

      if (response.ok && Array.isArray(promotions)) {
        setPromoCodeRows(
          promotions.map((promotion) => mapPromotionToRow(promotion, lookupResult.options))
        );
      }
    } finally {
      setIsPromotionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  function handlePromotionStatusChange(promotionId: string, isActive: boolean) {
    return api
      .updatePromotion(promotionId, { isActive })
      .then((response) => {
        if (!response.ok) return false;

        setPromoCodeRows((currentRows) =>
          currentRows.map((promoCode) =>
            promoCode.id === promotionId
              ? { ...promoCode, isActive, status: isActive ? 'Active' : 'Deactivated' }
              : promoCode
          )
        );
        return true;
      })
      .catch(() => false);
  }

  async function handleOpenAddPromoModal() {
    if (lookupOptions.area.length || lookupOptions.city.length || lookupOptions.property.length) {
      setIsAddPromoModalOpen(true);
      return;
    }

    setIsLookupsLoading(true);
    setLookupError('');

    try {
      const lookupResult = await loadPromoLookupOptions();

      setLookupOptions(lookupResult.options);

      if (lookupResult.hasError) {
        setLookupError('Some lookup options could not be loaded.');
      }
    } catch (error) {
      setLookupError('Lookup options could not be loaded.');
    } finally {
      setIsLookupsLoading(false);
      setIsAddPromoModalOpen(true);
    }
  }

  return (
    <div className="min-h-screen bg-white px-7 py-8 lg:px-12">
      <div className="mb-8 flex flex-row gap-4 justify-between">
        <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">Promo codes</h1>

        <button
          type="button"
          className="inline-flex w-fit items-center gap-3 rounded-lg bg-primary-600 px-6 py-4 text-lg font-gilroy-medium text-white shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          onClick={handleOpenAddPromoModal}
        >
          <AddIcon className="!h-7 !w-7" />
          Add new promo code
        </button>
      </div>

      <PromoCodesTable
        promoCodeRows={promoCodeRows}
        isPromotionsLoading={isPromotionsLoading}
        onStatusChange={handlePromotionStatusChange}
      />

      {isAddPromoModalOpen ? (
        <AddPromoCodeModal
          lookupOptions={lookupOptions}
          isLookupsLoading={isLookupsLoading}
          lookupError={lookupError}
          onClose={() => setIsAddPromoModalOpen(false)}
          onCreated={loadPromotions}
        />
      ) : null}
    </div>
  );
}
